import type { ChartType, Plugin } from 'chart.js'

import { setCursor } from '../utils/cursor'

const PLUGIN_ID = 'indicateClickOnBarHover'

const HOVER_SCALE = 1.1
const LERP_SPEED = 0.15

const lerp = (current: number, target: number, speed: number): number => {
  const next = current + (target - current) * speed

  return Math.abs(next - target) < 0.005 ? target : next
}

export const indicateClickOnBarHover: Plugin<ChartType> = {
  id: PLUGIN_ID,

  beforeDraw(chart) {
    const anyChart = chart as any
    const hoveredDatasetIndex = anyChart.$clickableDatasetIndex ?? null
    const hoveredIndex = anyChart.$clickableIndex ?? null
    const isHorizontal = chart.options.indexAxis === 'y'

    let animating = false

    for (let di = 0; di < chart.data.datasets.length; di++) {
      const meta = chart.getDatasetMeta(di)

      for (let i = 0; i < meta.data.length; i++) {
        const el = meta.data[i] as any
        const isHovered = di === hoveredDatasetIndex && i === hoveredIndex
        const target = isHovered ? HOVER_SCALE : 1.0
        const current = el.$hoverScale ?? 1.0

        // At rest, no scale needed
        if (current === target && current === 1.0) continue

        const next = lerp(current, target, LERP_SPEED)

        el.$hoverScale = next

        if (next !== target) animating = true
        if (next === 1.0) continue

        // Save originals, scale cross-axis only
        el.$orig = { height: el.height, width: el.width }

        if (isHorizontal) {
          el.height *= next
        } else {
          el.width *= next
        }
      }
    }

    if (animating && !anyChart.$hoverAnimFrame) {
      anyChart.$hoverAnimFrame = requestAnimationFrame(() => {
        anyChart.$hoverAnimFrame = null
        chart.draw()
      })
    }
  },

  afterDraw(chart) {
    for (let di = 0; di < chart.data.datasets.length; di++) {
      const meta = chart.getDatasetMeta(di)

      for (let i = 0; i < meta.data.length; i++) {
        const el = meta.data[i] as any

        if (!el.$orig) continue

        el.height = el.$orig.height
        el.width = el.$orig.width
        delete el.$orig
      }
    }
  },

  afterEvent(chart, args) {
    const e: any = args.event

    if (!e) return

    if (e.type === 'mouseout') {
      const prev = (chart as any).$clickableIndex ?? null

      if (prev !== null) {
        (chart as any).$clickableIndex = null
        ;(chart as any).$clickableDatasetIndex = null
        setCursor(chart, PLUGIN_ID, null)
        chart.update('none')
      }

      return
    }

    if (e.type !== 'mousemove') return

    const native = e.native

    if (!native) return

    const hits = chart.getElementsAtEventForMode(
      native as Event,
      'nearest',
      { intersect: true },
      true
    )

    const hit = hits.length
      ? (chart.data.datasets[hits[0].datasetIndex] as any).main
          ? hits[0]
          : null
      : null
    const nextDatasetIndex = hit ? hit.datasetIndex : null
    const nextIndex = hit ? hit.index : null

    const anyChart = chart as any
    const prevDatasetIndex = anyChart.$clickableDatasetIndex ?? null
    const prevIndex = anyChart.$clickableIndex ?? null

    if (prevDatasetIndex === nextDatasetIndex && prevIndex === nextIndex) return

    anyChart.$clickableDatasetIndex = nextDatasetIndex
    anyChart.$clickableIndex = nextIndex
    setCursor(chart, PLUGIN_ID, nextIndex !== null ? 'pointer' : null)

    chart.update('none')
  }
}
